/**
 * FormsTab — the study's forms (Phase C), inside StudyDetail. Lists forms
 * attached to this study with publish state + response counts; links into the
 * full builder at /crm/settings/forms/:id. Self-fetching (the study detail endpoint
 * doesn't carry forms), refreshed when the parent bumps reloadToken-style via
 * remount-free `onChanged` loads elsewhere — forms change rarely, so a fetch
 * per visit is fine.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClipboardList, Copy, Loader2, Plus, Search, X } from 'lucide-react';
import api from '../../../services/api';
import { CrmForm, StudyDetailResponse, studyInternalLabel } from '../../types';
import { relTime, useCanEditForms, FORM_SURFACES, formSurface, formSurfaceLabel, FormSurface } from '../../lib';

interface StudyChip { id: string | null; slug: string; title: string | null; internal_title?: string | null; }

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

function statusPill(status: string): string {
  switch (status) {
    case 'active': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
    case 'closed': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
    case 'archived': return 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
    default: return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
  }
}

interface FormsTabProps {
  detail: StudyDetailResponse;
  onChanged: () => void;
}

export default function FormsTab({ detail, onChanged }: FormsTabProps) {
  const navigate = useNavigate();
  // Item #04 (owner): forms are recruiter-editable — full parity, not admin-only.
  const canEdit = useCanEditForms();
  const studyId = detail.study.id;
  const [forms, setForms] = useState<CrmForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Item #05: which group is being created ('landing' | 'questionnaire' | null).
  const [creating, setCreating] = useState<FormSurface | null>(null);
  // Item #05: which per-study pointer is mid-save ('landing_form_id' | 'questionnaire_form_id').
  const [designating, setDesignating] = useState<string | null>(null);

  // "Use existing form" picker — copy any cataloged form into this study.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [catalog, setCatalog] = useState<CrmForm[]>([]);
  const [studyTitles, setStudyTitles] = useState<Record<string, string>>({});
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [copyingId, setCopyingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/crm/forms', { params: { study_id: studyId } });
      setForms(res.data?.forms || []);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [studyId]);

  useEffect(() => { load(); }, [load]);

  // Item #05: build a form in a specific group (landing vs questionnaire),
  // attached to this study, then open it in the builder.
  const createForSurface = async (surface: FormSurface) => {
    if (creating) return;
    setCreating(surface);
    setError(null);
    try {
      const label = surface === 'landing' ? 'landing page form' : 'study questionnaire';
      const res = await api.post('/api/crm/forms', {
        title: `${detail.study.title} — ${label}`,
        surface,
        study_id: studyId,
      });
      navigate(`/crm/settings/forms/${res.data.form.id}`);
    } catch (e: any) {
      setError(errMsg(e));
      setCreating(null);
    }
  };

  // Item #05: designate this study's per-group pointer (landing_form_id /
  // questionnaire_form_id). PATCHes the study and refreshes the parent detail.
  const designate = async (field: 'landing_form_id' | 'questionnaire_form_id', value: string) => {
    setDesignating(field);
    setError(null);
    try {
      await api.patch(`/api/crm/studies/${studyId}`, { [field]: value || null });
      onChanged();
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setDesignating(null);
    }
  };

  const openPicker = async () => {
    setPickerOpen(true);
    setSearch('');
    setPickerError(null);
    setPickerLoading(true);
    try {
      const [formsRes, studiesRes] = await Promise.all([
        api.get('/api/crm/forms'),
        api.get('/api/crm/studies'),
      ]);
      // Archived forms aren't reusable templates; everything else is fair game.
      setCatalog((formsRes.data?.forms || []).filter((f: CrmForm) => f.status !== 'archived'));
      const titles: Record<string, string> = {};
      for (const s of (studiesRes.data?.studies || []) as StudyChip[]) {
        if (s.id) titles[s.id] = studyInternalLabel(s);
      }
      setStudyTitles(titles);
    } catch (e: any) {
      setPickerError(errMsg(e));
    } finally {
      setPickerLoading(false);
    }
  };

  // Copy a chosen catalog form into THIS study (a new independent draft), then
  // open it so the admin can tailor it for this study before publishing.
  const copyIntoStudy = async (form: CrmForm) => {
    if (copyingId) return;
    setCopyingId(form.id);
    setPickerError(null);
    try {
      const res = await api.post(`/api/crm/forms/${form.id}/duplicate`, { study_id: studyId });
      navigate(`/crm/settings/forms/${res.data.form.id}`);
    } catch (e: any) {
      setPickerError(errMsg(e));
      setCopyingId(null);
    }
  };

  const filteredCatalog = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter((f) => f.title.toLowerCase().includes(q));
  }, [catalog, search]);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <ClipboardList className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          Forms
          <span className="text-sm font-normal text-gray-500 dark:text-gray-400">({forms.length})</span>
        </h3>
        {canEdit && (
          <button
            onClick={openPicker}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <Copy className="h-3.5 w-3.5" /> Use existing form
          </button>
        )}
      </div>

      {error && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : (
        // Item #05: two separately-built groups — the public ad landing-page form
        // and the texted study questionnaire — each with its own per-study
        // designation pointer and create flow.
        <div className="space-y-6">
          {FORM_SURFACES.map((group) => {
            const groupForms = forms.filter((f) => formSurface(f.surface) === group.value);
            const field = group.value === 'landing' ? 'landing_form_id' : 'questionnaire_form_id';
            const designatedId = (group.value === 'landing'
              ? detail.study.landing_form_id
              : detail.study.questionnaire_form_id) || '';
            const publishable = groupForms.filter((f) => f.status === 'active' && (f.version || 0) > 0);
            return (
              <section key={group.value}>
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{group.plural}</h4>
                  {canEdit && (
                    <button
                      onClick={() => createForSurface(group.value)}
                      disabled={!!creating}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-primary-600 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
                    >
                      {creating === group.value ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                      {group.value === 'landing' ? 'Build landing page form' : 'Build study questionnaire'}
                    </button>
                  )}
                </div>
                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                  {group.value === 'landing'
                    ? 'Served on the public WCR ad page (/study/<slug>).'
                    : 'Texted to leads for the 1/2/3 respond-options menu. Without one, this study falls back to the network-wide questionnaire.'}
                </p>

                {canEdit && (
                  <div className="mb-3">
                    <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                      {group.value === 'landing' ? 'Designated ad landing form' : 'Designated texted questionnaire'}
                    </label>
                    <select
                      value={designatedId}
                      disabled={designating === field}
                      onChange={(e) => designate(field, e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    >
                      <option value="">
                        {group.value === 'landing' ? 'None — ad landing page disabled' : 'None — use the network-wide questionnaire'}
                      </option>
                      {publishable.map((f) => <option key={f.id} value={f.id}>{f.title} (v{f.version})</option>)}
                    </select>
                    {designatedId && !publishable.some((f) => f.id === designatedId) && (
                      <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                        The designated form is not currently published — publish it below to activate it.
                      </p>
                    )}
                  </div>
                )}

                {groupForms.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No {group.plural.toLowerCase()} for this study yet.
                    {canEdit ? ` Build one${group.value === 'landing' ? ' (or import the WCR questionnaire from the Forms page)' : ''}.` : ''}
                  </p>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {groupForms.map((f) => (
                      <Link
                        key={f.id}
                        to={`/crm/settings/forms/${f.id}`}
                        className="flex items-center justify-between gap-3 py-3 transition-colors duration-100 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{f.title}</span>
                            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${statusPill(f.status)}`}>
                              {f.status}
                            </span>
                            {f.id === designatedId && (
                              <span className="shrink-0 rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                                designated
                              </span>
                            )}
                          </div>
                          <p className="mt-0.5 text-xs text-gray-400">
                            {f.version > 0 ? `v${f.version}` : 'never published'} · updated {relTime(f.updated_at)}
                          </p>
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{f.counts?.completed ?? 0}</p>
                          <p className="text-[11px] text-gray-400">completed</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {/* "Use existing form" picker — browse the whole catalog, copy one into this study. */}
      {pickerOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
          onClick={() => setPickerOpen(false)}
        >
          <div
            className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-t-xl bg-white shadow-xl dark:bg-gray-800 sm:rounded-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Use an existing form"
          >
            <div className="flex items-start justify-between gap-2 border-b border-gray-200 px-5 py-3 dark:border-gray-700">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Use an existing form</h3>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  Copies the form's questions &amp; settings into a new draft for this study. The original is untouched.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPickerOpen(false)}
                aria-label="Close"
                className="shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="border-b border-gray-200 px-5 py-2.5 dark:border-gray-700">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search forms by title…"
                  className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-8 pr-3 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
              {pickerError && (
                <div className="mx-3 mb-2 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                  {pickerError}
                </div>
              )}
              {pickerLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                </div>
              ) : filteredCatalog.length === 0 ? (
                <p className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  {catalog.length === 0 ? 'No forms in the catalog yet.' : 'No forms match your search.'}
                </p>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {filteredCatalog.map((f) => {
                    const sourceStudy = f.study_id
                      ? (f.study_id === studyId ? 'This study' : studyTitles[f.study_id] || 'Another study')
                      : 'Unassigned';
                    return (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => copyIntoStudy(f)}
                        disabled={!!copyingId}
                        className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition-colors duration-100 hover:bg-gray-50 disabled:opacity-60 dark:hover:bg-gray-700/40"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{f.title}</span>
                            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${statusPill(f.status)}`}>
                              {f.status}
                            </span>
                          </div>
                          <p className="mt-0.5 truncate text-xs text-gray-400">
                            {formSurfaceLabel(f.surface)}
                            {f.version > 0 ? ` · v${f.version}` : ' · never published'}
                            {` · ${sourceStudy}`}
                          </p>
                        </div>
                        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-primary-600 px-2.5 py-1 text-xs font-medium text-primary-700 dark:text-primary-400">
                          {copyingId === f.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
                          {copyingId === f.id ? 'Copying…' : 'Copy here'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
